import React, { useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Upload, Star, Trash2, Tag, Info } from 'lucide-react';
import { addContactImage, deleteContactImage, GalleryImage, listContactImages, setDefaultProfileImage, updateContactImage, uploadContactImage } from '../lib/images';
import { useState as useReactState } from 'react';
import { supabase } from '../lib/supabase';
import { getContactCore } from '../lib/contactsCore';
import { listEventsCore, listRelationshipsCore, getTodoByIdCore } from '@gv/core';

interface ContactGalleryModalProps {
    contactId: string;
    contactName: string;
    defaultUrl?: string | null;
    isOpen: boolean;
    onClose: () => void;
}

export default function ContactGalleryModal({ contactId, contactName, defaultUrl, isOpen, onClose }: ContactGalleryModalProps) {
    const [images, setImages] = useState<GalleryImage[]>([]);
    const [index, setIndex] = useState(0);
    const [groups, setGroups] = useState<Array<{ key: string; label: string; items: GalleryImage[] }>>([]);
    const [groupIndex, setGroupIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const fileRef = useRef<HTMLInputElement | null>(null);
    // Details are always visible per UX request
    const [detailsOpen, setDetailsOpen] = useState(true);
    const [sourceDetails, setSourceDetails] = useState<any | null>(null);
    const touchStart = useRef<{ x: number; y: number } | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            setLoading(true);
            try {
                const list = await listContactImages(contactId);
                // Also aggregate existing event/relationship images so older data appears
                const [events, rels] = await Promise.all([
                    listEventsCore(supabase as any, contactId),
                    listRelationshipsCore(supabase as any, contactId)
                ]);

                let arr = list.slice();
                // Merge events as synthetic entries
                const evRows = (events || []).filter(e => e.photoUrl);
                for (const e of evRows) {
                    const url = (e as any).photoUrl as string;
                    if (!url) continue;
                    if (!arr.some(i => i.url === url)) {
                        arr.push({ id: `ev_${e.id}`, contact_id: contactId, url, source_type: 'event', source_id: String(e.id), tags: [], created_at: undefined });
                    }
                }
                // Merge relationships as synthetic entries
                const relRows = (rels || []).filter(r => r.relatedAvatar);
                for (const r of relRows as any[]) {
                    const url = r.relatedAvatar as string;
                    if (!url) continue;
                    if (!arr.some(i => i.url === url)) {
                        arr.push({ id: `rel_${r.id}`, contact_id: contactId, url, source_type: 'relationship', source_id: String(r.id), tags: [], created_at: undefined });
                    }
                }
                // If defaultUrl exists but not in list yet, add as a synthetic first item for continuity
                if (defaultUrl && !arr.some(i => i.url === defaultUrl)) {
                    arr = [
                        { id: 'default', contact_id: contactId, url: defaultUrl, source_type: 'manual', source_id: null, tags: [], created_at: undefined },
                        ...arr,
                    ];
                }
                // Build groups by source (todo/event/relationship/manual)
                const map = new Map<string, GalleryImage[]>();
                const keyOf = (i: GalleryImage) => `${i.source_type}:${i.source_id || i.url}`;
                for (const it of arr) {
                    const k = keyOf(it);
                    if (!map.has(k)) map.set(k, []);
                    map.get(k)!.push(it);
                }
                const gs: Array<{ key: string; label: string; items: GalleryImage[] }> = [];
                for (const [k, items] of map) {
                    const [type, sid] = k.split(':');
                    let label = 'Photo';
                    if (type === 'todo') label = 'Follow up';
                    else if (type === 'event') label = 'Event';
                    else if (type === 'relationship') label = 'Relationship';
                    else label = 'Photo';
                    gs.push({ key: k, label, items });
                }
                // Keep manual/default first, then others by created order of first item
                gs.sort((a, b) => {
                    const aManual = a.key.startsWith('manual:');
                    const bManual = b.key.startsWith('manual:');
                    if (aManual !== bManual) return aManual ? -1 : 1;
                    const at = a.items[0]?.created_at || '';
                    const bt = b.items[0]?.created_at || '';
                    return (bt > at ? 1 : -1);
                });
                setGroups(gs);
                setImages(arr);
                setGroupIndex(0);
                setIndex(0);
            } catch (e) {
                setImages(defaultUrl ? [{ id: 'default', contact_id: contactId, url: defaultUrl, source_type: 'manual', source_id: null, tags: [], created_at: undefined }] : []);
            } finally {
                setLoading(false);
            }
        })();
    }, [isOpen, contactId, defaultUrl]);

    const currentGroup = groups[groupIndex]?.items || [];
    const current = currentGroup[index] || images[index];
    const canPrevImage = index > 0;
    const canNextImage = index < (currentGroup.length - 1);
    const canPrevGroup = groupIndex > 0;
    const canNextGroup = groupIndex < (groups.length - 1);

    // Load details for the current item when details panel is opened
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setSourceDetails(null);
            if (!detailsOpen || !current) return;
            try {
                if (current.source_type === 'event' && current.source_id) {
                    const all = await listEventsCore(supabase as any, contactId);
                    const found = (all || []).find(e => String(e.id) === String(current.source_id));
                    if (!cancelled) setSourceDetails(found ? {
                        id: found.id,
                        name: found.name,
                        when_date: found.when,
                        where_text: found.where,
                        notes: found.notes,
                    } : null);
                } else if (current.source_type === 'relationship' && current.source_id) {
                    const all = await listRelationshipsCore(supabase as any, contactId);
                    const found = (all || []).find(r => String((r as any).id) === String(current.source_id)) as any;
                    if (!cancelled) setSourceDetails(found ? {
                        id: found.id,
                        related_name: found.relatedName,
                        relation: found.relation,
                        where_met: found.whereMet,
                        when_met: found.whenMet,
                        notes: found.notes,
                    } : null);
                } else {
                    setSourceDetails(null);
                }
            } catch {
                if (!cancelled) setSourceDetails(null);
            }
        })();
        return () => { cancelled = true; };
    }, [detailsOpen, current]);

    const addTags = async (tags: string[]) => {
        if (!current || current.id === 'default') return;
        const uniq = Array.from(new Set([...(current.tags || []), ...tags.map(t => t.trim()).filter(Boolean)]));
        const updated = await updateContactImage(current.id, { tags: uniq });
        setImages(prev => prev.map(i => i.id === current.id ? updated : i));
    };

    const removeTag = async (tag: string) => {
        if (!current || current.id === 'default') return;
        const next = (current.tags || []).filter(t => t !== tag);
        const updated = await updateContactImage(current.id, { tags: next });
        setImages(prev => prev.map(i => i.id === current.id ? updated : i));
    };

    const onUpload = async (file: File) => {
        const url = await uploadContactImage(file, contactId);
        const saved = await addContactImage(contactId, url, { source_type: 'manual' });
        setImages(prev => [saved, ...prev]);
        setIndex(0);
    };

    const onTouchStart = (e: React.TouchEvent) => {
        const t = e.touches[0];
        touchStart.current = { x: t.clientX, y: t.clientY };
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (!touchStart.current) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - touchStart.current.x;
        const dy = t.clientY - touchStart.current.y;
        touchStart.current = null;
        const thresh = 30;
        if (Math.abs(dx) > Math.abs(dy)) {
            if (dx > thresh) {
                if (canPrevImage) setIndex(i => Math.max(0, i - 1));
                else if (canPrevGroup) { setGroupIndex(g => Math.max(0, g - 1)); setIndex(0); }
            } else if (dx < -thresh) {
                if (canNextImage) setIndex(i => Math.min(currentGroup.length - 1, i + 1));
                else if (canNextGroup) { setGroupIndex(g => Math.min(groups.length - 1, g + 1)); setIndex(0); }
            }
        } else {
            if (dy < -thresh) setDetailsOpen(true); // swipe up opens details
            else if (dy > thresh) setDetailsOpen(false); // swipe down closes details
        }
    };

    function TLDR({ contactId }: { contactId: string }) {
        const [tldr, setTldr] = React.useState<string | null>(null);
        React.useEffect(() => {
            let active = true;
            (async () => {
                try {
                    const row = await getContactCore(supabase as any, contactId);
                    if (!active) return;
                    setTldr((row as any)?.tldr_summary || null);
                } catch { if (active) setTldr(null); }
            })();
            return () => { active = false; };
        }, [contactId]);
        return (
            <div>
                <div className="text-gray-300 font-medium mb-1">Overview</div>
                <div className="text-sm text-gray-200 whitespace-pre-line">{tldr || 'No overview yet.'}</div>
            </div>
        );
    }

    function TodoDetails({ todoId }: { todoId: string }) {
        const [todo, setTodo] = React.useState<any | null>(null);
        React.useEffect(() => {
            let active = true;
            (async () => {
                try {
                    const data = await getTodoByIdCore(supabase as any, String(todoId));
                    if (!active) return;
                    setTodo(data || null);
                } catch { if (active) setTodo(null); }
            })();
            return () => { active = false; };
        }, [todoId]);
        if (!todo) return <div className="text-gray-400">No details available.</div>;
        return (
            <div>
                <div className="text-gray-300 font-medium mb-1">Follow up: {todo.title || 'Untitled'}</div>
                {todo.due_date && <div className="text-xs text-gray-400 mb-1">{new Date(todo.due_date).toLocaleString()}</div>}
                {todo.location && <div className="mb-1">Where: {todo.location}</div>}
                {todo.description && <div className="whitespace-pre-line">{todo.description}</div>}
            </div>
        );
    }

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[120]">
            <div className="absolute inset-0 bg-black/80" onClick={onClose} />
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                <div className="relative w-full max-w-3xl bg-gray-900 border border-gray-800 rounded-lg overflow-hidden shadow-2xl">
                    <div className="flex items-center justify-between p-3 border-b border-gray-800">
                        <div className="text-sm text-gray-300">{contactName} — Photos</div>
                        <div className="flex items-center gap-2">
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={async e => { const f = e.target.files?.[0]; if (f) await onUpload(f); }} />
                            <button onClick={() => fileRef.current?.click()} className="p-2 rounded bg-gray-800 border border-gray-700 text-gray-200 hover:bg-gray-700" title="Add photos"><Upload className="w-4 h-4" /></button>
                            <button onClick={onClose} className="p-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800"><X className="w-4 h-4" /></button>
                        </div>
                    </div>

                    <div className="relative bg-black" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                        {loading ? (
                            <div className="p-12 text-center text-gray-400">Loading…</div>
                        ) : images.length === 0 ? (
                            <div className="p-12 text-center text-gray-400">No photos yet. Add one.</div>
                        ) : (
                            <div className="relative">
                                <img src={current.url} alt="contact" className="w-full max-h-[60vh] object-contain bg-black" />
                                {(canPrevImage || canPrevGroup) && (
                                    <button onClick={() => { if (canPrevImage) setIndex(i => Math.max(0, i - 1)); else if (canPrevGroup) { setGroupIndex(g => Math.max(0, g - 1)); setIndex(0); } }} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 border border-gray-700 text-white"><ChevronLeft className="w-5 h-5" /></button>
                                )}
                                {(canNextImage || canNextGroup) && (
                                    <button onClick={() => { if (canNextImage) setIndex(i => Math.min(currentGroup.length - 1, i + 1)); else if (canNextGroup) { setGroupIndex(g => Math.min(groups.length - 1, g + 1)); setIndex(0); } }} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 border border-gray-700 text-white"><ChevronRight className="w-5 h-5" /></button>
                                )}
                                {/* Details are always visible now; remove explicit toggle */}
                            </div>
                        )}
                    </div>

                    {/* Actions, tags, and details */}
                    {current && (
                        <div className="p-3 border-t border-gray-800 bg-gray-900">
                            {/* Group header + pager */}
                            {groups.length > 1 && (
                                <div className="flex items-center justify-between mb-2 text-xs text-gray-400">
                                    <div>
                                        {groups[groupIndex]?.label === 'Photo' ? `${contactName} — Photos` :
                                            groups[groupIndex]?.label === 'Relationship' ? 'Relationships' :
                                                groups[groupIndex]?.label === 'Event' ? 'Events' : groups[groupIndex]?.label}
                                    </div>
                                    <div>{groupIndex + 1} / {groups.length}</div>
                                </div>
                            )}
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    {current.source_type === 'event' && (
                                        <button
                                            onClick={async () => { await setDefaultProfileImage(contactId, current.url); }}
                                            className="px-3 py-1.5 text-sm rounded bg-blue-600 border border-blue-500 text-white hover:bg-blue-500"
                                            title="Set as profile from event photo"
                                        >
                                            <Star className="w-4 h-4 inline mr-1" /> Set as Default
                                        </button>
                                    )}
                                    {current.id !== 'default' && (
                                        <button
                                            onClick={async () => { await deleteContactImage(current.id); setImages(prev => prev.filter(i => i.id !== current.id)); setIndex(i => Math.max(0, Math.min(i, images.length - 2))); }}
                                            className="px-3 py-1.5 text-sm rounded bg-red-700 border border-red-600 text-white hover:bg-red-600"
                                        >
                                            <Trash2 className="w-4 h-4 inline mr-1" /> Delete
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1 bg-gray-800 border border-gray-700 rounded px-2 py-1.5">
                                        <Tag className="w-4 h-4 text-gray-300" />
                                        <input
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyDown={async (e) => {
                                                if (e.key === 'Enter' && tagInput.trim()) {
                                                    const tags = tagInput.split(',').map(s => s.trim()).filter(Boolean);
                                                    await addTags(tags);
                                                    setTagInput('');
                                                }
                                            }}
                                            placeholder="Add tags… (comma or Enter)"
                                            className="bg-transparent outline-none text-sm text-gray-200 placeholder-gray-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {current.tags && current.tags.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {current.tags.map(t => (
                                        <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300 text-xs">
                                            {t}
                                            <button className="ml-1 text-gray-400 hover:text-white" onClick={() => removeTag(t)}>×</button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Source details panel */}
                            {detailsOpen && (
                                <div className="mt-3 p-3 rounded bg-gray-800 border border-gray-700 text-sm text-gray-200">
                                    {/* Swipe-down TLDR when profile image group (manual/default) */}
                                    {current.source_type === 'manual' && !current.source_id ? (
                                        <TLDR contactId={contactId} />
                                    ) : current.source_type === 'todo' ? (
                                        <TodoDetails todoId={current.source_id as string} />
                                    ) : current.source_type === 'event' ? (
                                        sourceDetails ? (
                                            <div>
                                                <div className="text-gray-300 font-medium mb-1">{contactName} — Photos</div>
                                                <div className="text-xs text-gray-500 mb-2">Events</div>
                                                <div className="text-gray-300 font-medium mb-1">{sourceDetails.name || 'Untitled'}</div>
                                                <div className="text-xs text-gray-400 mb-1">{sourceDetails.when_date ? new Date(sourceDetails.when_date).toLocaleString() : ''}</div>
                                                {sourceDetails.where_text && <div className="mb-1">Where: {sourceDetails.where_text}</div>}
                                                {sourceDetails.notes && <div className="whitespace-pre-line">{sourceDetails.notes}</div>}
                                            </div>
                                        ) : (
                                            <div className="text-gray-400">No details available.</div>
                                        )
                                    ) : current.source_type === 'relationship' ? (
                                        sourceDetails ? (
                                            <div>
                                                <div className="text-gray-300 font-medium mb-1">{contactName} — Photos</div>
                                                <div className="text-xs text-gray-500 mb-2">Relationships</div>
                                                <div className="text-gray-300 font-medium mb-1">{sourceDetails.related_name}</div>
                                                {sourceDetails.relation && <div className="mb-1">Role: {sourceDetails.relation}</div>}
                                                <div className="text-xs text-gray-400 mb-1">{sourceDetails.when_met ? new Date(sourceDetails.when_met).toLocaleDateString() : ''}</div>
                                                {sourceDetails.where_met && <div className="mb-1">Where: {sourceDetails.where_met}</div>}
                                                {sourceDetails.notes && <div className="whitespace-pre-line">{sourceDetails.notes}</div>}
                                            </div>
                                        ) : (
                                            <div className="text-gray-400">No details available.</div>
                                        )
                                    ) : (
                                        <div>
                                            <div className="text-gray-300 font-medium mb-1">{contactName} — Photos</div>
                                            <div className="text-xs text-gray-500 mb-2">Photos</div>
                                            <div className="text-gray-400">Manual photo</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
